import { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Capacitor } from "@capacitor/core";
import { StatusBar, Style } from "@capacitor/status-bar";
import { SplashScreen } from "@capacitor/splash-screen";
import { installVoiceIntentBridge } from "./voiceIntents";

/**
 * Thin wrapper that wires native-only behavior into the React tree:
 *  - Routes Siri / Assistant deep-links to React Router.
 *  - Hides the Capacitor splash once React has mounted (we'd rather show
 *    the Suspense fallback than a static splash for too long).
 *  - Sets the status bar style to match our header.
 *
 * No-op on web — every call short-circuits via Capacitor.isNativePlatform().
 */
export function NativeShellBridge() {
  const navigate = useNavigate();

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;

    let disposed = false;
    let cleanup: (() => void) | null = null;

    (async () => {
      const bridge = await installVoiceIntentBridge((path) => {
        navigate(path, { replace: false });
      });
      if (disposed) {
        bridge.dispose();
        return;
      }
      cleanup = bridge.dispose;

      // Don't await — splash hide is fire-and-forget.
      SplashScreen.hide({ fadeOutDuration: 200 }).catch(() => {});
      StatusBar.setStyle({ style: Style.Light }).catch(() => {});
    })();

    return () => {
      disposed = true;
      cleanup?.();
    };
  }, [navigate]);

  return null;
}
